import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import FileRef from "./FileRef";
import { deepFreeze, timestampToDate } from "../util/misc";
import { cloneDeep, every, get, isEmpty, omit } from "lodash";
import { diff } from "deep-object-diff";

export const MESSAGE_FIELDS = {
  text: "",
  replyTo: null,
  topicID: null,
  seen: {},
  to: [],
  // The project files posted with the message (see Message.fileEntry): its
  // images, shown in the text, and any other attachments.
  files: [],

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(MESSAGE_FIELDS);

export default class Message extends DataStructure {
  constructor(projectID, id, data = {}) {
    data = sanitize(data, MESSAGE_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.projectID = projectID || null;
    this.permissions = Object.freeze({
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
    });
  }

  // Permissions

  canBeEditedBy(user) {
    return user.isEmployee && (user.id === this.data.createdBy || user.isAdmin);
  }

  canBeDeletedBy(user) {
    return user.isEmployee && (user.id === this.data.createdBy || user.isAdmin);
  }

  // Database

  get path() {
    return Message.getPath(this.projectID, this.id);
  }

  static getPath(projectID, id) {
    let path = `projects/${projectID}/messages`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(issue) {
    return issue.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Message(snapshot.ref.parent.parent.id, snapshot.id, data);
  }

  // Formatting

  get isOnlyEmoji() {
    return this.data.text.length <= 3 && /^\p{Emoji}+$/u.test(this.data.text);
  }

  get wasSeen() {
    return this.data.seen[store.state.main.user.id];
  }

  get wasSent() {
    return this.data.createdBy === store.state.main.user.id;
  }

  get wasReceived() {
    return !this.wasSent;
  }

  get isToMe() {
    return this.data.to.includes(store.state.main.user.id);
  }

  get project() {
    return this.projectID ? store.state.main.projects[this.projectID] : null;
  }

  get topic() {
    return this.data.topicID
      ? store.state.main.topics[this.data.topicID]
      : null;
  }

  get replyTo() {
    return this.data.replyTo
      ? store.state.main.messages[this.data.replyTo]
      : null;
  }

  get to() {
    return this.data.to.map((id) => store.state.main.users[id]);
  }

  // A message's record of one of its project files. `inline` images appear in
  // the text as <img data-file-id>; the message keeps one only while its text
  // still shows it.
  static fileEntry(projectFile, inline = false) {
    const file = projectFile.data.file || {};
    return {
      id: projectFile.id,
      storagePath: projectFile.data.storagePath || "",
      inline: Boolean(inline),
      name: file.name || "",
      type: file.type || "",
      size: file.size || 0,
      extension: file.extension || "",
      url: file.url || "",
      modified: file.modified || null,
    };
  }

  // The IDs of the files the text shows inline.
  static inlineFileIDs(text) {
    return [...String(text || "").matchAll(/data-file-id="([^"]+)"/g)].map(
      (match) => match[1]
    );
  }

  get fileRefs() {
    return this.data.files.map((file) => new FileRef(file));
  }

  get createdBy() {
    return this.data.createdBy && store.state?.main?.users
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy && store.state?.main?.users
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  get text() {
    return this.data.text.replace(
      /(<span class="user-ref" data-id=")([^\"]+)(" contenteditable="false">@)([^<]+)(<\/span>)/g,
      (match, openingTagPrefix, id, openingTagSuffix, name, closingTag) => {
        return (
          openingTagPrefix +
          id +
          openingTagSuffix +
          store.state.main.users[id].name +
          closingTag
        );
      }
    );
  }
}
